import React, { useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import { X, WhatsappLogo } from "@phosphor-icons/react";

const MAX = 100;
const REASON = { cooldown: "Sent in the last 10 min", ineligible: "Not eligible", failed: "Send failed" };

export default function BulkWhatsAppModal({ open, leadIds, onClose, onDone }) {
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    if (!open) return null;
    const tooMany = leadIds.length > MAX;

    async function submit() {
        setSending(true);
        try {
            const r = await api.post("/crm/bulk/leads/whatsapp-login-link", { lead_ids: leadIds });
            setResult(r.data);
            toast.success(`WhatsApp link sent to ${r.data.counts.sent} lead(s)`);
        } catch (e) {
            logError("bulk whatsapp failed", e);
            toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Bulk WhatsApp failed");
        } finally { setSending(false); }
    }

    const close = () => { if (result) onDone?.(); else onClose?.(); setResult(null); };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={close} data-testid="crm-bulk-wa-modal">
            <div className="w-full max-w-lg bg-white border-2 border-ink p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                            Bulk action · {leadIds.length} lead{leadIds.length === 1 ? "" : "s"}
                        </p>
                        <h3 className="font-heading font-black text-lg mt-1 flex items-center gap-2">
                            <WhatsappLogo size={18} weight="bold" className="text-emerald-600" /> Send login links on WhatsApp
                        </h3>
                    </div>
                    <button onClick={close} aria-label="Close" className="p-1 hover:bg-neutral-100" data-testid="crm-bulk-wa-close">
                        <X size={16} />
                    </button>
                </div>

                {!result ? (
                    <>
                        <p className="text-sm text-neutral-700 mb-4">
                            Leads with an email get a 30-day login link; phone-only leads get a sign-up link with their number pre-filled.
                            Already-promoted, opted-out and recently-messaged leads are skipped automatically.
                        </p>
                        {tooMany && (
                            <p className="text-xs font-bold text-red-600 mb-3" data-testid="crm-bulk-wa-too-many">
                                Select up to {MAX} leads per send.
                            </p>
                        )}
                    </>
                ) : (
                    <ResultSummary result={result} />
                )}

                <div className="flex justify-end gap-2">
                    <button onClick={close} className="border-2 border-ink px-4 py-2 text-xs font-bold uppercase" data-testid="crm-bulk-wa-cancel">
                        {result ? "Done" : "Cancel"}
                    </button>
                    {!result && (
                        <button
                            onClick={submit}
                            disabled={sending || tooMany}
                            data-testid="crm-bulk-wa-submit"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase disabled:opacity-40 transition-colors"
                        >
                            {sending ? "Sending…" : `Send to ${leadIds.length}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ResultSummary({ result }) {
    const c = result.counts;
    return (
        <div className="mb-4 space-y-3" data-testid="crm-bulk-wa-result">
            <div className="grid grid-cols-4 gap-2 text-center">
                {[["sent", "Sent"], ["cooldown", "Cooldown"], ["ineligible", "Skipped"], ["failed", "Failed"]].map(([k, l]) => (
                    <div key={k} className="border border-ink p-2">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{l}</p>
                        <p className="font-heading font-black text-xl" data-testid={`crm-bulk-wa-count-${k}`}>{c[k] || 0}</p>
                    </div>
                ))}
            </div>
            {result.skipped.length > 0 && (
                <ul className="max-h-40 overflow-auto text-xs divide-y divide-neutral-200 border border-neutral-200">
                    {result.skipped.map((s) => (
                        <li key={s.id} className="px-2 py-1.5 flex justify-between gap-2">
                            <span className="font-bold truncate">{s.name || s.id.slice(0, 8)}</span>
                            <span className="text-neutral-500 truncate" title={s.detail}>{REASON[s.reason]} · {s.detail}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
